/**
 * CR500: Results Meta Block
 * CR283: Enhanced with typography controls and template support.
 * 
 * Displays configurable results count text above the deal grid.
 * Supports custom templates with {count} and {dealWord} tokens,
 * plus font family, size, and alignment controls.
 * 
 * Config options:
 * - showCount: boolean - Show the deal count
 * - countStyle: "compact" | "headline" - Display style
 * - labelText: string - Label text (e.g., "Deals Available")
 * - showFoundText: boolean - Show "found" when filtering
 * - template: string - Text template with {count} and {dealWord} tokens
 * - fontFamily: string - Font family ID from fontRegistry
 * - fontSize: number - Font size in pixels
 * - align: "left" | "center" | "right" - Text alignment
 */

import { useMemo } from "react";
import type { BlockProps } from "./types";
import type { ResultsMetaBlockConfig } from "@/lib/layout-studio/types";
import { DEFAULT_RESULTS_META_CONFIG } from "@/lib/layout-studio/types";
import { inferDealCategory } from "@/lib/types/deals";
import { resolveFontFamily } from "@/lib/theme/fontRegistry";
import { getMerchant, getAllMerchants } from "@/lib/merchantIndex";

/**
 * Normalize a merchant display name to a key format for lookup
 */
function normalizeToMerchantKey(merchantName: string): string {
  return merchantName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Get the promo merchant for a deal (for filtering/grouping).
 */
function getPromoMerchant(deal: { promo_merchant_name?: string; merchant: string }): string {
  return deal.promo_merchant_name || deal.merchant;
}

/**
 * Get category for a deal using multiple fallback strategies
 */
function getDealCategory(deal: { promo_merchant_name?: string; merchant: string }): string | null {
  const merchantName = getPromoMerchant(deal);
  if (!merchantName) return null;
  
  const normalizedKey = normalizeToMerchantKey(merchantName);
  
  // Strategy 1: Direct lookup by normalized key
  const merchant = getMerchant(normalizedKey);
  if (merchant?.category) {
    return merchant.category;
  }
  
  // Strategy 2: Try exact match on display_name
  const allMerchants = getAllMerchants();
  for (const m of allMerchants) {
    if (m.display_name.toLowerCase() === merchantName.toLowerCase()) {
      if (m.category) return m.category;
    }
  }
  
  // Strategy 3: Try partial match
  for (const m of allMerchants) {
    const displayLower = m.display_name.toLowerCase();
    const nameLower = merchantName.toLowerCase();
    if (displayLower.includes(nameLower) || nameLower.includes(displayLower)) {
      if (m.category) return m.category;
    }
  }
  
  return null;
}

export function ResultsMetaBlock({ config, context }: BlockProps<ResultsMetaBlockConfig>) {
  // Merge with defaults
  const mergedConfig: ResultsMetaBlockConfig = {
    ...DEFAULT_RESULTS_META_CONFIG,
    ...config,
  };

  const { deals, searchQuery, selectedCategory, selectedDealTypes, loading } = context;

  // CR283: Count displayed deals with same filtering logic as ResultsBlock
  const filteredCount = useMemo(() => {
    let result = [...deals];

    // Filter by merchant category
    if (selectedCategory !== "All") {
      result = result.filter((deal) => {
        const dealCat = getDealCategory(deal);
        if (selectedCategory === "Unclassified") {
          return dealCat === null;
        }
        return dealCat === selectedCategory;
      });
    }

    // Filter by deal type
    if (selectedDealTypes !== "All") {
      result = result.filter((deal) => {
        const dealCategory = inferDealCategory(deal);
        return selectedDealTypes.includes(dealCategory);
      });
    }

    // Filter by search
    if (searchQuery.trim().length >= 1) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((deal) => {
        const promoMerchant = getPromoMerchant(deal);
        if (promoMerchant.toLowerCase().includes(query)) return true;
        
        const brand = deal.qualifying_product_name || deal.purchase_gift_card_brand || deal.gift_card_brand;
        if (brand?.toLowerCase().includes(query)) return true;
        
        if (deal.reward_brand_name?.toLowerCase().includes(query)) return true;
        if (deal.bonus_merchant?.toLowerCase().includes(query)) return true;
        if (deal.raw_source_notes?.toLowerCase().includes(query)) return true;
        if (deal.fine_print?.toLowerCase().includes(query)) return true;
        if (deal.headline_mechanic?.toLowerCase().includes(query)) return true;
        return false;
      });
    }

    return result.length;
  }, [deals, searchQuery, selectedCategory, selectedDealTypes]);

  // Don't show if disabled or still loading (avoid flicker)
  if (!mergedConfig.showCount || loading) {
    return null;
  }

  // Don't render if no deals at all
  if (deals.length === 0) {
    return null;
  }

  // CR283: Build display text using template or legacy logic
  const count = filteredCount;
  const dealWord = count === 1 ? "deal" : "deals";
  
  let displayText = "";
  
  if (mergedConfig.template) {
    // Use template with token replacement
    displayText = mergedConfig.template
      .replace("{count}", String(count))
      .replace("{dealWord}", dealWord);
  } else {
    // Legacy logic for backward compatibility
    const plural = count !== 1 ? "s" : "";
    const isSearching = searchQuery.trim().length >= 1;
    const isFiltered = isSearching || selectedCategory !== "All" || selectedDealTypes !== "All";
    
    if (mergedConfig.countStyle === "compact") {
      displayText = `${count} deal${plural}`;
    } else {
      if (isSearching && mergedConfig.showFoundText) {
        displayText = `${count} Result${plural}`;
      } else if (isFiltered && mergedConfig.showFoundText) {
        displayText = `${count} Deal${plural} Found`;
      } else {
        displayText = `${count} ${mergedConfig.labelText}`;
      }
    }
  }

  if (!displayText) {
    return null;
  }

  // CR283: Resolve typography styles
  const fontFamily = mergedConfig.fontFamily 
    ? resolveFontFamily(mergedConfig.fontFamily) 
    : undefined;
  const fontSize = mergedConfig.fontSize || 14;
  const textAlign = mergedConfig.align || "left";

  const alignmentClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[textAlign];

  return (
    <div className={`py-2 ${alignmentClass}`}>
      <span
        className="text-muted-foreground"
        style={{
          fontFamily,
          fontSize: `${fontSize}px`,
        }}
      >
        {displayText}
      </span>
    </div>
  );
}
